import { driver } from "driver.js"
import "driver.js/dist/driver.css"
import "./tour.css"

const DEFAULT_WAIT_MS = 6000
const CLICK_HINT = '<div class="mt-tour-hint">Click the highlighted element to continue.</div>'
const BUTTON_HINT = '<div class="mt-tour-hint">The tour continues when you click the button.</div>'
const INTERACTIVE_CLASS = "mt-tour-interactive"

// Mehrere Anker (Array) = "einer davon"
const anchorSelector = (names) =>
  [names].flat().map((name) => `[data-tour="${name}"]`).join(", ")

export const stepSelector = (step) =>
  step.selector ?? (step.anchor ? anchorSelector(step.anchor) : null)

// getClientRects ist leer bei display:none (auch geerbt) – der Planner wird nur per CSS versteckt
export function findVisible(selector) {
  for (const el of document.querySelectorAll(selector)) {
    if (el.getClientRects().length > 0) return el
  }
  return undefined
}

// Ragt das Ziel über seinen Scrollbereich hinaus, wird der sichtbare Bereich markiert statt des ganzen Elements
function fitToScrollArea(el) {
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const { overflowY } = getComputedStyle(p)
    if (overflowY !== "auto" && overflowY !== "scroll") continue
    return el.getBoundingClientRect().height > p.clientHeight + 1 ? p : el
  }
  return el
}

function followLayout(el, refresh) {
  let frame = null
  const schedule = () => {
    frame ??= requestAnimationFrame(() => {
      frame = null
      refresh()
    })
  }
  const observer = new ResizeObserver(schedule)
  observer.observe(el)
  if (el.parentElement) observer.observe(el.parentElement)
  document.addEventListener("transitionend", schedule, true)
  return () => {
    cancelAnimationFrame(frame)
    observer.disconnect()
    document.removeEventListener("transitionend", schedule, true)
  }
}

const waitsForUser = (step) => Boolean(step.click || step.until)

const WATCH_THROTTLE_MS = 200

function watchUntil(selector, onFound) {
  let done = false
  let timer = null
  const check = () => {
    timer = null
    if (done || !findVisible(selector)) return
    done = true
    observer.disconnect()
    onFound()
  }
  // Karte und Zeitslider mutieren ständig; findVisible erzwingt Layout, daher gedrosselt
  const observer = new MutationObserver(() => {
    timer ??= setTimeout(check, WATCH_THROTTLE_MS)
  })
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true })
  timer = setTimeout(check, 0)
  return () => {
    done = true
    clearTimeout(timer)
    observer.disconnect()
  }
}

function watchClick(selector, onClick) {
  const handler = (e) => {
    if (e.target instanceof Element && e.target.closest(selector)) setTimeout(onClick, 0)
  }
  document.addEventListener("click", handler, true)
  return () => document.removeEventListener("click", handler, true)
}

function unlock(specs) {
  for (const spec of specs) {
    for (const el of document.querySelectorAll(stepSelector(spec))) el.classList.add(INTERACTIVE_CLASS)
  }
}

function relock() {
  for (const el of document.querySelectorAll(`.${INTERACTIVE_CLASS}`)) el.classList.remove(INTERACTIVE_CLASS)
}

function hintFor(step) {
  if (typeof step.click === "string") return BUTTON_HINT
  return step.click ? CLICK_HINT : ""
}

function toDriveStep(step, index, steps, interactive) {
  const selector = stepSelector(step)
  const skipSelector = step.skipIf ? anchorSelector(step.skipIf) : null
  const untilSelector = step.until ? anchorSelector(step.until) : null
  // driver.js wartet sonst bis zum Timeout, wenn das Ziel erst während des Wartens obsolet wird
  const skipNow = () =>
    Boolean((skipSelector && findVisible(skipSelector)) || (untilSelector && findVisible(untilSelector)))
  const canGoBack = index > 0 && !waitsForUser(steps[index - 1])
  // Fehlt das Ziel eines Klick-/Warte-Schritts, erscheint er mittig – dann braucht er ein Next
  const targetMissing = () => Boolean(selector) && !findVisible(selector)
  const buttons = () => [
    ...(waitsForUser(step) && !targetMissing() ? [] : ["next"]),
    ...(canGoBack ? ["previous"] : []),
    "close",
  ]

  let stopWatching = null
  let stopFollowing = null
  const driveStep = {
    popover: {
      title: step.title,
      description: `${step.text}${hintFor(step)}`,
      side: step.side,
      align: step.align,
      get showButtons() {
        return buttons()
      },
    },
    advanceOnClick: step.click === true,
    skipMissingElement: Boolean(step.optional || skipSelector || untilSelector),
    onHighlighted: (el, _step, { driver: d }) => {
      unlock(interactive)
      if (el && el.id !== "driver-dummy-element") stopFollowing = followLayout(el, () => d.refresh())
      if (untilSelector) stopWatching = watchUntil(untilSelector, () => d.moveNext())
      else if (typeof step.click === "string")
        stopWatching = watchClick(anchorSelector(step.click), () => d.moveNext())
    },
    onDeselected: () => {
      stopWatching?.()
      stopFollowing?.()
      stopWatching = null
      stopFollowing = null
    },
  }

  if (selector) {
    driveStep.element = () => {
      if (skipNow()) return undefined
      const el = findVisible(selector)
      return el && fitToScrollArea(el)
    }
    Object.defineProperty(driveStep, "waitForElement", {
      get: () => (skipNow() ? 0 : (step.wait ?? DEFAULT_WAIT_MS)),
      enumerable: true,
    })
  }
  return driveStep
}

export function createTourDriver(tour, { onComplete, onEnd }) {
  return driver({
    steps: tour.steps.map((step, i, steps) => toDriveStep(step, i, steps, tour.interactive ?? [])),
    showProgress: true,
    progressText: "{{current}} of {{total}}",
    nextBtnText: "Next",
    prevBtnText: "Back",
    doneBtnText: "Done",
    popoverClass: "mt-tour",
    stagePadding: 6,
    stageRadius: 6,
    smoothScroll: true,
    allowKeyboardControl: false,
    overlayClickBehavior: () => {},
    onDoneClick: (_el, _step, { driver: d }) => {
      onComplete()
      d.destroy()
    },
    onDestroyed: () => {
      relock()
      onEnd()
    },
  })
}
